import {
    Observable,
    map
} from "rxjs";

import {
    Injectable,
    inject
} from "@angular/core";

import {
    ResourceBundle,
    ISearchSort,
    SearchSortDirection
} from "@shopliftr/common-js/data";

import { ModelAction } from "@shopliftr/common-js/data";

import {
    BulkResponse,
    BulkUtil,
    CreateRequest,
    CreateResponse,
    ResourceBundleSearchFilterOptions,
    DeleteRequest,
    DeleteResponse,
    GetResponse,
    RequestUtil,
    SearchRequest,
    SearchResponse,
    UpdateRequest,
    UpdateResponse
} from "@shopliftr/common-js/shared";

import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";
import { IFullAccessInternalApiClientService } from "../../../../interfaces/internal-api-client";
import { ServiceException } from "../../../../exceptions/service-exception";

/**
 * Service for all CRUD operations related to the ResourceBundle model.
 * Implements {@link IFullAccessInternalApiClientService} providing full CRUD access including
 * bulk operations for the ResourceBundle model via the Internal API v2.
 *
 * @implements {IFullAccessInternalApiClientService<ResourceBundle>}
 * @pattern IFullAccessInternalApiClientService
 * @providedIn root
 */
@Injectable({ providedIn: "root" })
export class ResourceBundleApiClientService implements IFullAccessInternalApiClientService<ResourceBundle> {

    readonly basePath = "resource-bundle";
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieves multiple ResourceBundle entities by their unique identifiers in a single bulk request.
     *
     * @param ids - Array of unique identifiers of the ResourceBundle entities to retrieve
     * @returns Observable that emits a BulkResponse containing the requested entities
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<ResourceBundle>(id, ResourceBundle.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Deletes multiple ResourceBundle entities in a single bulk request.
     *
     * @param items - Array of delete requests specifying the ResourceBundle entities to delete
     * @returns Observable that emits a BulkResponse with the result of each delete operation
     * @throws {ServiceException} When no delete requests are provided
     */
    bulkDelete$(items: Array<DeleteRequest>): Observable<BulkResponse> {

        if (!items?.length) {
            throw new ServiceException("Could not perform a bulk operation with no requests");
        }

        const bulkRequest = BulkUtil.createBulkRequest(items.map((request: DeleteRequest) => {

            return BulkUtil.createRequestItem(request);
        }));

        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Updates multiple ResourceBundle entities in a single bulk request.
     *
     * @param requests - Array of update requests specifying the ResourceBundle entities and fields to update
     * @returns Observable that emits a BulkResponse with the result of each update operation
     * @throws {ServiceException} When no update requests are provided
     */
    bulkUpdate$(
        requests: Array<UpdateRequest<ResourceBundle>>
    ): Observable<BulkResponse> {

        if (!requests?.length) {
            throw new ServiceException("Could not perform a bulk operation with no requests");
        }

        const bulkRequest = BulkUtil.createBulkRequest(requests.map(
            (request: UpdateRequest<ResourceBundle>) => {

                return BulkUtil.createRequestItem(request);
            }
        ));

        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Creates multiple resource-bundle entities in a single bulk request.
     *
     * @param items Array of create requests for resource bundles to create
     * @returns Observable that emits a BulkResponse with the results of each create operation
     */
    bulkCreate$(items: Array<CreateRequest<ResourceBundle, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Creates a new ResourceBundle entity with the provided field values.
     *
     * @param deal - The ResourceBundle model containing the field values for the new entity
     * @returns Observable that emits the created ResourceBundle entity
     */
    create$(deal: ResourceBundle): Observable<ResourceBundle> {

        const request: CreateRequest<ResourceBundle> = RequestUtil.createCreateRequest(
            ModelAction.ClientCreate,
            deal
        );

        return this._internalApiRequestService.create$(
            request,
            this.basePath
        ).pipe(map((response: CreateResponse<ResourceBundle>) => response.payload));
    }

    /**
     * Deletes a single ResourceBundle entity by its unique identifier.
     *
     * @param id - Unique identifier of the ResourceBundle entity to delete
     * @param version - Version of the entity for optimistic concurrency control
     * @returns Observable that emits the DeleteResponse confirming the deletion
     */
    delete$(id: string, version: number): Observable<DeleteResponse> {

        const request = RequestUtil.createDeleteRequest<void>(id, ResourceBundle.modelName, ModelAction.ClientDelete, version);

        return this._internalApiRequestService.delete$(request, this.basePath);
    }

    /**
     * Retrieves a single ResourceBundle entity by its unique identifier.
     *
     * @param id - Unique identifier of the ResourceBundle entity to retrieve
     * @returns Observable that emits the matching ResourceBundle entity
     */
    get$(id: string): Observable<ResourceBundle> {

        const request = RequestUtil.createGetRequest<void>(id, ResourceBundle.modelName, ModelAction.ClientGet);

        return this._internalApiRequestService.get$<ResourceBundle>(request, this.basePath)
            .pipe(map((response: GetResponse<ResourceBundle>) => response.payload));
    }

    /**
     * Retrieves all ResourceBundle entities matching the given filter options.
     * Results are sorted by type ascending by default when no sort is provided.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param sort - Optional sort configuration; defaults to ascending by type
     * @returns Observable that emits an array of all matching ResourceBundle entities
     */
    getAll$(filterOptions: ResourceBundleSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<ResourceBundle>> {

        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, ResourceBundle.modelName, undefined, undefined, filterOptions, sort ?? [{
                field: "type",
                direction: SearchSortDirection.Ascending
            }]
        );

        return this._internalApiRequestService.getAll$<ResourceBundle, ResourceBundleSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Searches for ResourceBundle entities using the given filter options and pagination parameters.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param page - Zero-based page index for pagination
     * @param pageSize - Number of results to return per page
     * @param sort - Optional sort configuration for the result set
     * @returns Observable that emits a paginated SearchResponse containing the matching ResourceBundle entities
     */
    search$(
        filterOptions: ResourceBundleSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<ResourceBundle>> {

        const request: SearchRequest<ResourceBundleSearchFilterOptions> = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            ResourceBundle.modelName,
            (page !== undefined && pageSize !== undefined) ? page * pageSize : undefined,
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$(request, this.basePath);
    }

    /**
     * Updates a ResourceBundle entity with the specified field changes.
     *
     * @param id - Unique identifier of the ResourceBundle entity to update
     * @param version - Version of the entity for optimistic concurrency control
     * @param fields - ResourceBundle model containing the updated field values
     * @param removeFields - Optional array of field names to remove from the entity
     * @returns Observable that emits the updated ResourceBundle entity
     */
    update$(
        id: string,
        version: number,
        fields: ResourceBundle,
        removeFields: Array<string> | undefined
    ): Observable<ResourceBundle> {

        const request = RequestUtil.createUpdateRequest<ResourceBundle, void>(id, ModelAction.ClientUpdate, fields, version, removeFields);

        return this._internalApiRequestService.update$(request, this.basePath).pipe(map(
            (response: UpdateResponse<ResourceBundle>) => response.payload
        ));
    }

    /**
     * Updates the record of the original resource bundle with the updated model provided.
     *
     * @param original The original resource bundle model
     * @param updated The updated resource bundle model with changes applied
     * @returns Observable that emits the updated resource bundle
     */
    updateWithChanges$(original: ResourceBundle, updated: ResourceBundle): Observable<ResourceBundle> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }
}
